import clsx from 'clsx';
import { DatePicker } from 'components/Form/DatePicker/DatePicker';
import { ImageInput } from 'components/Form/ImageInput/ImageInput';
import { Input } from 'components/Form/Input/Input';
import { NumberInput } from 'components/Form/NumberInput/NumberInput';
import { RadioGroup } from 'components/Form/RadioGroup/RadioGroup';
import { SelectAutocomplete } from 'components/Form/SelectAutocomplete/SelectAutocomplete';
import { PetVaccinesForm } from 'components/Pet/Form/Vaccines/PetVaccinesForm';
import { VaccineRecordErrorModal } from 'components/Pet/VaccineRecordErrorModal/VaccineRecordErrorModal';
import { Button, ButtonVariant } from 'components/UI/Button/Button';
import { useMarketplace } from 'hooks/useMarketplace';
import { usePetFormData } from 'hooks/usePetFormData';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { getOnlineBookingCustomer } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { IntakeFormCustomQuestion } from 'types/IntakeForm';
import { emptyPet, Pet, PetBreed, PetHairLength, PetTypeIds } from 'types/Pet';
import {
    getPetSizeByWeight,
    getVaccineIssues,
    requiredVaccines,
    VaccineIssues
} from 'utils/petUtils';
import classes from './PetForm.module.scss';

interface PetFormProps {
    pet?: Pet;
    loading?: boolean;
    showSubmitButton?: boolean;
    submitButtonLabel?: string;
    submitButtonColor?: ButtonVariant;
    cancelButtonLabel?: string;
    questions?: Array<IntakeFormCustomQuestion>;
    className?: string;
    /**
     * Online Booking only: enforce that all required marketplace vaccines are
     * present and not expired, require the birthday, keep the submit button
     * always enabled and show inline validation messages. Off by default so
     * Intake Form / Client Form keep their current behavior.
     */
    enforceRequirements?: boolean;
    onChange?: (isValid: boolean) => void;
    onCancel?: () => void;
    onSubmit?: (pet: Pet) => void;
}

const buildVaccineErrorMessage = (issues: VaccineIssues): string => {
    const parts: Array<string> = [];

    if (issues.missing.length > 0) {
        parts.push(
            `Add a valid expiration date for: ${issues.missing.map(v => v.name).join(', ')}`
        );
    }

    if (issues.expired.length > 0) {
        parts.push(`Update the expired vaccine(s): ${issues.expired.map(v => v.name).join(', ')}`);
    }

    return parts.join('. ');
};

export const PetForm = React.forwardRef<{ submit: () => void }, PetFormProps>(
    (
        {
            pet,
            loading,
            showSubmitButton,
            submitButtonLabel,
            submitButtonColor,
            cancelButtonLabel,
            questions,
            className,
            enforceRequirements,
            onChange,
            onCancel,
            onSubmit
        },
        ref
    ) => {
        const customer = useSelector(getOnlineBookingCustomer);
        const marketplace = useMarketplace();

        const {
            watch,
            control,
            register,
            setValue,
            handleSubmit,
            formState: { errors, isValid }
        } = useForm<Pet>({
            defaultValues: pet ?? emptyPet(Number(customer.id)),
            mode: 'onChange'
        });

        const formValues = watch();

        const petRequiredVaccines = requiredVaccines(marketplace, formValues.type?.id);

        const { types, sizes, hairLengths, breeds, genders, hairColors } = usePetFormData(
            formValues.type?.id
        );

        const formRef = React.useRef<HTMLFormElement>(null);
        const vaccinesRef = React.useRef<HTMLDivElement>(null);

        const uploadAgainHandler = () => {
            vaccinesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        };

        React.useEffect(() => {
            onChange?.(isValid);
            // eslint-disable-next-line
        }, [isValid]);

        React.useEffect(() => {
            const subscription = watch((value, { name }) => {
                if (['weight', 'type'].includes(String(name)) && value.type?.id) {
                    const petSize = getPetSizeByWeight(Number(value.weight), value.type?.id, sizes);

                    petSize && setValue('size', petSize);
                }

                if (name === 'type') {
                    setValue('breed', null as unknown as PetBreed);

                    if (value.type?.id === PetTypeIds.EXOTIC) {
                        setValue('hairLength', {} as PetHairLength);
                    }
                }
            });

            return () => subscription.unsubscribe();
            // eslint-disable-next-line
        }, [watch, sizes]);

        React.useImperativeHandle(ref, () => ({
            submit: () => {
                handleSubmit(submitHandler)();
            }
        }));

        const questionChangeHandler = (
            index: number,
            question: IntakeFormCustomQuestion,
            value: string
        ) => {
            setValue(`answers.${index}`, {
                question: question.question,
                questionId: question.id,
                answer: value
            });
        };

        const submitHandler = (data: Pet) => {
            onSubmit?.(data);
        };

        const fieldError = (message?: string) =>
            enforceRequirements && message ? <p className={classes.fieldError}>{message}</p> : null;

        return (
            <form
                ref={formRef}
                className={clsx(classes.root, className)}
                onSubmit={handleSubmit(submitHandler)}
            >
                <div className={classes.container}>
                    <Controller
                        name="image"
                        control={control}
                        render={({ field }) => (
                            <ImageInput
                                id="image"
                                value={field.value?.src}
                                label="Upload a photo of your pet"
                                className={classes.imageInput}
                                onChange={(file, src) => field.onChange({ file, src })}
                            />
                        )}
                    />

                    <div className="w-full">
                        <Controller
                            name="type"
                            control={control}
                            rules={{ validate: value => !!value?.id || 'Pet type is required' }}
                            render={({ field }) => (
                                <RadioGroup
                                    label="Pet type"
                                    options={types}
                                    value={field.value?.id}
                                    className={classes.radioGroup}
                                    onChange={value =>
                                        field.onChange({
                                            id: value.id,
                                            singular: value.name,
                                            plural: value.description ?? ''
                                        })
                                    }
                                />
                            )}
                        />
                        {fieldError(errors.type?.message)}

                        {formValues.type?.id && (
                            <>
                                <Input
                                    required
                                    label="Pet name"
                                    error={!!errors.name}
                                    {...register('name', { required: 'Pet name is required' })}
                                />
                                {fieldError(errors.name?.message)}
                                <div className={classes.grid}>
                                    <div>
                                        <Controller
                                            name="breed"
                                            control={control}
                                            rules={{
                                                required:
                                                    formValues.type?.id === PetTypeIds.EXOTIC
                                                        ? 'Specie is required'
                                                        : 'Breed is required'
                                            }}
                                            render={({ field }) => (
                                                <SelectAutocomplete
                                                    required
                                                    label={
                                                        formValues.type?.id === PetTypeIds.EXOTIC
                                                            ? 'Specie'
                                                            : 'Breed'
                                                    }
                                                    options={breeds}
                                                    value={field.value}
                                                    error={!!errors.breed}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                        {fieldError(errors.breed?.message)}
                                    </div>
                                    <div>
                                        <Controller
                                            name="weight"
                                            control={control}
                                            rules={{
                                                required: 'Weight is required',
                                                min: {
                                                    value: 1,
                                                    message: 'Weight must be greater than 0'
                                                }
                                            }}
                                            render={({ field }) => (
                                                <NumberInput
                                                    {...field}
                                                    required
                                                    integerOnly
                                                    label="Weight"
                                                    error={!!errors.weight}
                                                    inputProps={{
                                                        maxLength: 3
                                                    }}
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {fieldError(errors.weight?.message)}
                                    </div>
                                </div>
                                <div className={classes.grid}>
                                    <div>
                                        <Controller
                                            name="gender"
                                            control={control}
                                            rules={{ required: 'Gender is required' }}
                                            render={({ field }) => (
                                                <SelectAutocomplete
                                                    required
                                                    label="Gender"
                                                    options={genders}
                                                    value={field.value}
                                                    error={!!errors.gender}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                        {fieldError(errors.gender?.message)}
                                    </div>
                                    <div>
                                        <Controller
                                            name="hairLength"
                                            control={control}
                                            rules={{
                                                validate: value => {
                                                    if (
                                                        formValues.type?.id !== 'exotics' &&
                                                        !value
                                                    ) {
                                                        return 'Hair length is required';
                                                    }
                                                    return true;
                                                }
                                            }}
                                            render={({ field }) => (
                                                <SelectAutocomplete
                                                    label="Hair Length"
                                                    value={field.value}
                                                    error={!!errors.hairLength}
                                                    required={formValues.type?.id !== 'exotics'}
                                                    options={hairLengths}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                        {fieldError(errors.hairLength?.message)}
                                    </div>
                                </div>
                                <div className={classes.grid}>
                                    <div>
                                        <Controller
                                            name="birthdate"
                                            control={control}
                                            rules={{
                                                validate: value =>
                                                    !enforceRequirements || value
                                                        ? true
                                                        : 'Birthday is required'
                                            }}
                                            render={({ field }) => (
                                                <DatePicker
                                                    required={enforceRequirements}
                                                    label="Birthday"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        />
                                        {fieldError(errors.birthdate?.message)}
                                    </div>

                                    <Controller
                                        name="hairColor"
                                        control={control}
                                        render={({ field }) => (
                                            <SelectAutocomplete
                                                label="Color"
                                                value={field.value}
                                                options={hairColors}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {formValues.type?.id && petRequiredVaccines.length > 0 && (
                        <div ref={vaccinesRef}>
                            <Controller
                                name="vaccines"
                                control={control}
                                render={({ field }) => (
                                    <PetVaccinesForm
                                        key={formValues.type?.id}
                                        requiredVaccines={petRequiredVaccines}
                                        petVaccines={pet?.vaccines ?? []}
                                        onChange={field.onChange}
                                    />
                                )}
                                rules={{
                                    validate: value => {
                                        if (
                                            !enforceRequirements ||
                                            !marketplace.settings.requireVaccinesEnabled
                                        ) {
                                            if (value?.some(v => !v.expiration)) {
                                                return 'Expiration date is required';
                                            }
                                            return true;
                                        }

                                        const issues = getVaccineIssues(
                                            value ?? [],
                                            petRequiredVaccines
                                        );

                                        return buildVaccineErrorMessage(issues) || true;
                                    }
                                }}
                            />
                            {fieldError(errors.vaccines?.message)}
                        </div>
                    )}

                    {formValues.type?.id && questions && (
                        <div className="flex flex-col gap-5">
                            {questions.map((question, index) => (
                                <Input
                                    multiline
                                    key={question.id}
                                    label={question.question}
                                    required={question.isRequired}
                                    placeholder="Write your answer"
                                    {...register(`answers.${index}.answer`, {
                                        required: question.isRequired,
                                        onChange: e =>
                                            questionChangeHandler(index, question, e.target.value)
                                    })}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {showSubmitButton && (
                    <div
                        className={clsx(
                            'w-full',
                            petRequiredVaccines.length === 0 && !questions?.length && 'mt-8'
                        )}
                    >
                        <div className={onCancel ? classes.actions : undefined}>
                            <Button
                                type="submit"
                                variant={submitButtonColor ?? 'black'}
                                loading={loading}
                                disabled={enforceRequirements ? !!loading : !isValid}
                                onClick={handleSubmit(submitHandler)}
                            >
                                {submitButtonLabel ?? 'Submit'}
                            </Button>

                            {onCancel && (
                                <Button type="button" variant="outlined" onClick={onCancel}>
                                    {cancelButtonLabel ?? 'Cancel'}
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                <VaccineRecordErrorModal onUploadAgain={uploadAgainHandler} />
            </form>
        );
    }
);

PetForm.displayName = 'PetForm';
